import { useTranslation } from "react-i18next";
import "@/index.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { lazy, Suspense } from "react";
import { Toaster } from "sonner";
import { ThemeProvider } from "@/context/ThemeContext";
import { AuthProvider } from "@/context/AuthContext";
import RequireAuth from "@/components/app/RequireAuth";
import RequireProfile from "@/components/app/RequireProfile";
import ProfileHomeRedirect from "@/components/app/ProfileHomeRedirect";
import RequireAdmin from "@/components/app/RequireAdmin";
import InstallAppPrompt from "@/components/app/InstallAppPrompt";
import PageLoader from "@/components/app/PageLoader";
import ErrorBoundary from "@/components/app/ErrorBoundary";

// Route-level code splitting: every page is its own webpack chunk, loaded on
// first navigation instead of all at once in the initial bundle.
const LandingPage = lazy(() => import("@/pages/LandingPage"));
const AuthPage = lazy(() => import("@/pages/AuthPage"));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));
const TeamManagePage = lazy(() => import("@/pages/registration/TeamManagePage"));
const ExplorePage = lazy(() => import("@/pages/ExplorePage"));
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const TournamentsListPage = lazy(() =>
  import("@/pages/TournamentsPage").then((m) => ({ default: m.TournamentsListPage })),
);
const NewTournamentRedirect = lazy(() =>
  import("@/pages/TournamentsPage").then((m) => ({ default: m.NewTournamentRedirect })),
);
const TournamentDetailPage = lazy(() => import("@/pages/TournamentDetailPage"));
const PublicTournamentPage = lazy(() => import("@/pages/PublicTournamentPage"));
const TournamentKioskPage = lazy(() => import("@/pages/TournamentKioskPage"));
const PaymentsPage = lazy(() =>
  import("@/pages/OverviewPages").then((m) => ({ default: m.PaymentsPage })),
);
const SchedulePage = lazy(() =>
  import("@/pages/OverviewPages").then((m) => ({ default: m.SchedulePage })),
);
const TeamsOverviewPage = lazy(() =>
  import("@/pages/OverviewPages").then((m) => ({ default: m.TeamsOverviewPage })),
);
const StatsOverviewPage = lazy(() =>
  import("@/pages/OverviewPages").then((m) => ({ default: m.StatsOverviewPage })),
);
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const AccountPage = lazy(() => import("@/pages/AccountPage"));
const HelpPage = lazy(() => import("@/pages/HelpPage"));
const CookiesPage = lazy(() =>
  import("@/pages/LegalPages").then((m) => ({ default: m.CookiesPage })),
);
const LegalNoticePage = lazy(() =>
  import("@/pages/LegalPages").then((m) => ({ default: m.LegalNoticePage })),
);
const OrganizerTermsPage = lazy(() =>
  import("@/pages/LegalPages").then((m) => ({ default: m.OrganizerTermsPage })),
);
const PrivacyPage = lazy(() =>
  import("@/pages/LegalPages").then((m) => ({ default: m.PrivacyPage })),
);
const TermsPage = lazy(() =>
  import("@/pages/LegalPages").then((m) => ({ default: m.TermsPage })),
);
const PricingPage = lazy(() => import("@/pages/PricingPage"));
const RefereeMarketplacePage = lazy(() => import("@/pages/RefereeMarketplacePage"));
const RefereeGuidePage = lazy(() => import("@/pages/RefereeGuidePage"));
const EventsServicePage = lazy(() => import("@/pages/EventsServicePage"));
const ProfileSelectPage = lazy(() => import("@/pages/ProfileSelectPage"));
const PlayerDashboardPage = lazy(() => import("@/pages/PlayerDashboardPage"));
const RefereeResultsPage = lazy(() => import("@/pages/RefereeResultsPage"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));

const protectedPage = (page) => <RequireAuth>{page}</RequireAuth>;
const profilePage = (profiles, page) => (
  <RequireAuth>
    <RequireProfile allow={profiles}>{page}</RequireProfile>
  </RequireAuth>
);

function App() {
  const { t } = useTranslation();
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <a
            href="#main-content"
            className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[200] focus:rounded-full focus:bg-primary focus:px-4 focus:py-2 focus:text-primary-foreground focus:shadow-lg"
            data-testid="skip-link"
          >{t("interface.skipContent")} </a>
          <ErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<AuthPage mode="login" />} />
              <Route path="/register" element={<AuthPage mode="register" />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/explore" element={<ExplorePage />} />
              <Route path="/t/:slug" element={<PublicTournamentPage />} />
              <Route path="/t/:slug/kiosk" element={<TournamentKioskPage />} />
              <Route path="/t/:slug/manage" element={<TeamManagePage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/legal" element={<LegalNoticePage />} />
              <Route path="/cookies" element={<CookiesPage />} />
              <Route path="/organizers" element={<OrganizerTermsPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/arbitros" element={<RefereeGuidePage />} />
              <Route path="/eventos" element={<EventsServicePage />} />

              <Route path="/app" element={protectedPage(<ProfileHomeRedirect />)} />
              <Route path="/app/select-profile" element={protectedPage(<ProfileSelectPage />)} />
              <Route path="/app/organizer" element={profilePage(["organizer"], <DashboardPage />)} />
              <Route
                path="/app/tournaments"
                element={profilePage(["organizer"], <TournamentsListPage />)}
              />
              <Route
                path="/app/tournaments/new"
                element={profilePage(["organizer"], <NewTournamentRedirect />)}
              />
              <Route
                path="/app/tournaments/:id"
                element={profilePage(["organizer"], <TournamentDetailPage />)}
              />
              <Route
                path="/app/teams"
                element={profilePage(["organizer"], <TeamsOverviewPage />)}
              />
              <Route path="/app/schedule" element={profilePage(["organizer"], <SchedulePage />)} />
              <Route path="/app/stats" element={profilePage(["organizer"], <StatsOverviewPage />)} />
              <Route path="/app/payments" element={profilePage(["organizer"], <PaymentsPage />)} />
              <Route
                path="/app/following"
                element={profilePage(["player"], <PlayerDashboardPage />)}
              />
              <Route path="/app/explore" element={profilePage(["player"], <ExplorePage embedded />)} />
              <Route path="/app/account" element={protectedPage(<AccountPage />)} />
              <Route path="/app/help" element={protectedPage(<HelpPage />)} />
              <Route
                path="/app/referees"
                element={profilePage(["organizer", "referee"], <RefereeMarketplacePage />)}
              />
              <Route
                path="/app/referees/assignments/:applicationId"
                element={profilePage(["referee"], <RefereeResultsPage />)}
              />

              <Route path="/admin" element={<RequireAdmin><AdminPage /></RequireAdmin>} />

              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
          </ErrorBoundary>
          <InstallAppPrompt />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
